
import { formatSample } from "../../map/data/formattingData";
import { getLastUpdatedSampleTimestamp, getLatestSample } from "../../map/data/latestData";
import { DataSample, DataSampleType, DataSampleUnit, Station } from "../types";
import { formatDate, getHourText } from "../utils/index";
import { getHistoricalData } from "../../services/valuesApi";
import { speedAndDirectionChart, lockXAxis, roseChart, toHighchartsXY } from "./chart/windChart";
import Highcharts from "highcharts";
import { getFadeValue } from "../utils/colors";
import { setQueryValue } from "../utils/urlUtils";
import { getImageTimestamp, getLastImageUrl } from "../../services/api";
import { getCurrentView, getViewUnit } from "../storage/viewsStore";
import { SampleGroup } from "./SearchControl";
import { temperatureChart } from "./chart/TemperatureChart";
import { pressureChart } from "./chart/pressureChart";

let currentStation: Station | null = null;
const sidebarDOM = document.getElementById('sidebar') as HTMLElement;
const btnCloseDOM = document.getElementById('sb-close') as HTMLButtonElement;
const rangeSelectDOM = document.getElementById("time-range") as HTMLSelectElement;
const windChartDOM = document.getElementById("windChart");
const currentChartDOM = document.getElementById("currentChart");
const temperatureChartDOM = document.getElementById("tempChart");
const pressureChartDOM = document.getElementById("pressureChart");
const windRoseDOM = document.getElementById("windRose");
let windRose: Highcharts.Chart | null;
let windChart: Highcharts.Chart | null;
let tempChart: Highcharts.Chart | null;
let pressChart: Highcharts.Chart | null;
let seaCurrentChart: Highcharts.Chart | null;

const title = document.getElementById("sidebar-title");
const owner = document.getElementById("sidebar-owner");
const lastUpdate = document.getElementById("sidebar-lastUpdate");
const nowPanel = document.getElementById("panel-now");
const histPanel = document.getElementById("panel-historical");
const cameraPanel = document.getElementById("panel-camera");


const handleX = document.getElementById('sb-handle-x') as HTMLElement | null;
const handleY = document.getElementById('sb-handle-y') as HTMLElement | null;
const header = document.getElementById('sb-header') as HTMLElement | null;
const mmMobile = window.matchMedia('(max-width: 640px)');
const MOBILE_SNAPS = [0.5, 0.7, 1] as const; // peek, medium, full
let snapIdx = 0;

export function openSidebar(s: Station) {
	onSideBarOpening(s);
	openSidebarPeek();
}

export function closeSidebar() {
	currentStation = null;
	sidebarDOM.classList.remove('open');

	windRose = destroyChart(windRose);
	windChart = destroyChart(windChart);
	windChart = destroyChart(windChart);
	tempChart = destroyChart(tempChart);
	seaCurrentChart = destroyChart(seaCurrentChart);
}

function destroyChart(chart: Highcharts.Chart | null) {
	windRose?.destroy();
	return null;
}

// Close button & ESC
btnCloseDOM?.addEventListener('pointerdown', e => e.stopPropagation());
btnCloseDOM?.addEventListener('click', closeSidebar);
document.addEventListener('keydown', (e) => {
	if (e.key === 'Escape' && sidebarDOM.classList.contains('open')) {
		closeSidebar()
	};
});

/* Desktop width bounds (px) */
const MIN_W = 280;          // px
const MAX_W = 8000;          // px hard cap
const MAX_W_VW = 0.95;      // 90% of viewport

/* Mobile height bounds (ratios of viewport height) */
const MIN_H_DVH = 0.40;     // 40dvh
const MAX_H_DVH = 1.00;     // 100dvh (fullscreen)

function effectiveMaxWidth() {
	return Math.min(window.innerWidth * MAX_W_VW, MAX_W);
}
function effectiveMinWidth() {
	return MIN_W;
}
function effectiveMinHeight() {
	return Math.floor(window.innerHeight * MIN_H_DVH);
}
function effectiveMaxHeight() {
	return Math.floor(window.innerHeight * MAX_H_DVH);
}
function clamp(n: number, min: number, max: number) {
	return Math.max(min, Math.min(max, n));
}

window.addEventListener('resize', () => {
	// If current size exceeds new bounds, snap back inside
	if (!mmMobile.matches) {
		const current = sidebarDOM.getBoundingClientRect().width;
		const next = clamp(current, effectiveMinWidth(), effectiveMaxWidth());
		sidebarDOM.style.setProperty('--sb-width', `${next}px`);
	} else {
		const current = sidebarDOM.getBoundingClientRect().height;
		const next = clamp(current, effectiveMinHeight(), effectiveMaxHeight());
		sidebarDOM.style.setProperty('--sb-height', `${next}px`);
	}
	if (mmMobile.matches) {
		updateMobileFullscreenClass();
	}
});
function startResizeX(pointerId: number, startX: number) {
	if (mmMobile.matches) return;
	const startRect = sidebarDOM.getBoundingClientRect();
	const startWidth = startRect.width;
	sidebarDOM.classList.add('resizing');
	document.body.classList.add('resizing-ew');

	const onMove = (e: PointerEvent) => {
		const dx = startX - e.clientX; // drag left => bigger
		const next = clamp(Math.round(startWidth + dx), effectiveMinWidth(), effectiveMaxWidth());
		sidebarDOM.style.setProperty('--sb-width', `${next}px`);
	};
	const onUp = (e: PointerEvent) => {
		window.removeEventListener('pointermove', onMove);
		window.removeEventListener('pointerup', onUp);
		try { handleX?.releasePointerCapture(pointerId); } catch { }
		sidebarDOM.classList.remove('resizing');
		document.body.classList.remove('resizing-ew');
	};
	window.addEventListener('pointermove', onMove, { passive: true });
	window.addEventListener('pointerup', onUp, { once: true });
}

function startResizeY(pointerId: number, startY: number, element: HTMLElement) {
	if (!mmMobile.matches) return;
	const startRect = sidebarDOM.getBoundingClientRect();
	const startHeight = startRect.height;
	sidebarDOM.classList.add('resizing');
	document.body.classList.add('resizing-ns');

	const onMove = (e: PointerEvent) => {
		const dy = startY - e.clientY; // drag up => taller
		const next = clamp(Math.round(startHeight + dy), effectiveMinHeight(), effectiveMaxHeight());
		sidebarDOM.style.setProperty('--sb-height', `${next}px`);
		updateMobileFullscreenClass();
	};
	const onUp = (e: PointerEvent) => {
		window.removeEventListener('pointermove', onMove);
		window.removeEventListener('pointerup', onUp);
		try { element?.releasePointerCapture(pointerId); } catch { }
		sidebarDOM.classList.remove('resizing');
		document.body.classList.remove('resizing-ns');
		updateMobileFullscreenClass();
	};
	window.addEventListener('pointermove', onMove, { passive: true });
	window.addEventListener('pointerup', onUp, { once: true });
}

handleX?.addEventListener('pointerdown', (e) => {
	e.preventDefault(); e.stopPropagation();
	handleX.setPointerCapture(e.pointerId);
	startResizeX(e.pointerId, e.clientX);
});
handleY?.addEventListener('pointerdown', (e) => {
	e.preventDefault(); e.stopPropagation();
	handleY.setPointerCapture(e.pointerId);
	startResizeY(e.pointerId, e.clientY, handleY);
});
header?.addEventListener('pointerdown', (e) => {
	if ((e.target as HTMLElement).closest('#sb-close')) return;
	e.preventDefault(); e.stopPropagation();
	header.setPointerCapture(e.pointerId);
	startResizeY(e.pointerId, e.clientY, header);
});


rangeSelectDOM.addEventListener("change", async () => {
	initializeHistoricalView(Number(rangeSelectDOM.value));
});

sidebarDOM.style.setProperty('--sb-width', `30%`);
sidebarDOM.style.setProperty('--sb-max-width', `${MAX_W}px`);

async function initializeNowCharts(maxAgeHours: number) {
	if (currentStation) {
		if (windRoseDOM) {
			windRoseDOM.classList.add("hidden");
			if (currentStation.HasWindData && maxAgeHours) {
				var windSpeedUnit = getViewUnit(DataSampleType.WIND_SPEED, DataSampleUnit.METERS_PER_SECOND);
				var windDirectionUnit = getViewUnit(DataSampleType.WIND_DIRECTION, DataSampleUnit.DEGREE);
				windRoseDOM.classList.remove("hidden");
				windRose = roseChart.create({ containerId: windRoseDOM.id, data: [], maxAgeHours: maxAgeHours, turboThreshold: 0 }, "Wind", windSpeedUnit, windDirectionUnit);
				const data = await loadWindData(currentStation, new Date(Date.now() - (maxAgeHours * 60 * 60 * 1000)), false);
				const points = [];
				if (data?.speed && data?.dir) {
					for (var i = 0; i < data.speed.length; i++) {
						const speed = data.speed[i] as DataSample;
						const direction = data.dir[i] as DataSample;
						if (speed && direction !== undefined) {
							points.push(roseChart.createRosePoint(direction, speed, maxAgeHours));
						}
					}
				}
				windRose.series[0].setData(points);
			}
		}
	}
}


const CHARTNAME_BY_TYPE: Partial<Record<DataSampleType, string>> = {
	[DataSampleType.WIND_SPEED]: "Wind speed",
	[DataSampleType.WIND_DIRECTION]: "Wind direction",
	[DataSampleType.WIND_GUST_SPEED]: "Gust speed",
	[DataSampleType.WIND_GUST_DIRECTION]: "Gust direction",
	[DataSampleType.CURRENT_SPEED]: "Current speed",
	[DataSampleType.AIRTEMP]: "Air temperature",
	[DataSampleType.SEATEMP]: "Sea temperature",
	[DataSampleType.PRESSURE_QNH]: "Sea Level",
	[DataSampleType.PRESSURE_QFE_PO]: "Ground Level"
};

async function initializeHistoricalView(hours: number) {
	const from = new Date(Date.now() - hours * 3600 * 1000);

	if (currentStation) {
		if (windChartDOM) {
			windChartDOM.classList.add("hidden");
			if (currentStation.HasWindData) {
				var windSpeedUnit = getViewUnit(DataSampleType.WIND_SPEED, DataSampleUnit.METERS_PER_SECOND);
				var windDirectionUnit = getViewUnit(DataSampleType.WIND_DIRECTION, DataSampleUnit.DEGREE);
				var windGustSpeedUnit = getViewUnit(DataSampleType.WIND_GUST_SPEED, DataSampleUnit.METERS_PER_SECOND);
				var windGustDirectionUnit = getViewUnit(DataSampleType.WIND_GUST_DIRECTION, DataSampleUnit.DEGREE);
				windChartDOM.classList.remove("hidden");
				windChart = speedAndDirectionChart.create({ containerId: windChartDOM.id, historyChartIntervalHours: hours }, "Wind", windSpeedUnit, windDirectionUnit);
				windChart.showLoading("Loading");
				const data = await loadWindData(currentStation, from);
				if (data?.speed) {
					windChart.addSeries(speedAndDirectionChart.speedSeries(false, CHARTNAME_BY_TYPE[DataSampleType.WIND_SPEED], data?.speed ?? [], windSpeedUnit));
				}
				if (data?.gustSpeed) {
					windChart.addSeries(speedAndDirectionChart.speedSeries(true, CHARTNAME_BY_TYPE[DataSampleType.WIND_GUST_SPEED], data?.gustSpeed ?? [], windGustSpeedUnit));
				}
				if (data?.dir) {
					windChart.addSeries(speedAndDirectionChart.directionSeries(false, CHARTNAME_BY_TYPE[DataSampleType.WIND_DIRECTION], data?.dir ?? [], windDirectionUnit));
				}
				if (data?.gustDirection) {
					windChart.addSeries(speedAndDirectionChart.directionSeries(true, CHARTNAME_BY_TYPE[DataSampleType.WIND_GUST_DIRECTION], data?.gustDirection ?? [], windGustDirectionUnit));
				}
				if (!data?.gustDirection && !data.dir) {
					windChart.axes[2].remove(false);
				}
				windChart.hideLoading();
				lockXAxis(windChart, from.getTime());
				windChart.redraw();
			}
		}
		if (temperatureChartDOM) {
			temperatureChartDOM.classList.add("hidden");
			if (currentStation.HasAirTemperature || currentStation.HasSeaTemperature) {
				temperatureChartDOM.classList.remove("hidden");
				tempChart = temperatureChart.create({ containerId: temperatureChartDOM.id, historyChartIntervalHours: hours }, getViewUnit(DataSampleType.AIRTEMP, DataSampleUnit.CELSIUS));
				tempChart.showLoading("Loading");
				const data = await loadTemperatureData(currentStation, from);
				if (data?.air) {
					tempChart.addSeries(temperatureChart.temperatureSeries(CHARTNAME_BY_TYPE[DataSampleType.AIRTEMP], data?.air ?? [], getViewUnit(DataSampleType.AIRTEMP, DataSampleUnit.CELSIUS)));
				}
				if (data?.sea) {
					tempChart.addSeries(temperatureChart.temperatureSeries(CHARTNAME_BY_TYPE[DataSampleType.SEATEMP], data?.sea ?? [], getViewUnit(DataSampleType.SEATEMP, DataSampleUnit.CELSIUS)));
				}
				tempChart.hideLoading();
				lockXAxis(tempChart, from.getTime());
				tempChart.redraw();
			}
		}
		if (pressureChartDOM) {
			pressureChartDOM.classList.add("hidden");
			if (currentStation.HasPressure) {
				pressureChartDOM.classList.remove("hidden");
				pressChart = pressureChart.create({ containerId: pressureChartDOM.id, historyChartIntervalHours: hours }, getViewUnit(DataSampleType.PRESSURE_QFE_PO, DataSampleUnit.mBar));
				pressChart.showLoading("Loading");
				const data = await loadPressureData(currentStation, from);
				if (data?.stationLevel) {
					pressChart.addSeries(pressureChart.pressureSeries(CHARTNAME_BY_TYPE[DataSampleType.PRESSURE_QFE_PO], data?.stationLevel ?? [], getViewUnit(DataSampleType.PRESSURE_QFE_PO, DataSampleUnit.mBar)));
				}
				if (data?.seaLevel) {
					pressChart.addSeries(pressureChart.pressureSeries(CHARTNAME_BY_TYPE[DataSampleType.PRESSURE_QNH], data?.seaLevel ?? [], getViewUnit(DataSampleType.PRESSURE_QNH, DataSampleUnit.mBar)));
				}
				pressChart.hideLoading();
				lockXAxis(pressChart, from.getTime());
				pressChart.redraw();
			}
		}
		if (currentChartDOM) {
			currentChartDOM.classList.add("hidden");
			if (currentStation.HasCurrentData) {
				currentChartDOM.classList.remove("hidden");
				seaCurrentChart = speedAndDirectionChart.create({ containerId: currentChartDOM.id, historyChartIntervalHours: hours }, "Current", getViewUnit(DataSampleType.CURRENT_SPEED, DataSampleUnit.METERS_PER_SECOND), getViewUnit(DataSampleType.CURRENT_DIRECTION, DataSampleUnit.DEGREE));
				seaCurrentChart.showLoading("Loading");
				const data = await loadCurrentData(currentStation, from);
				if (data?.speed) {
					seaCurrentChart.addSeries(speedAndDirectionChart.speedSeries(false, "Current speed", data?.speed ?? [], getViewUnit(DataSampleType.CURRENT_SPEED, DataSampleUnit.METERS_PER_SECOND)));
				}
				if (data?.dir) {
					seaCurrentChart.addSeries(speedAndDirectionChart.directionSeries(false, "Current direction", data?.dir ?? [], getViewUnit(DataSampleType.CURRENT_DIRECTION, DataSampleUnit.DEGREE)));
				} else {
					seaCurrentChart.axes[2].remove(false);
				}
				seaCurrentChart.hideLoading();
				lockXAxis(seaCurrentChart, from.getTime());
				seaCurrentChart.redraw();
			}
		}
	}
}
async function loadWindData(s: Station, from: Date, includeGust = true) {
	try {
		const measurements = s.Measurements.filter(o => o.IsHistValue);
		const getId = (t: DataSampleType) => measurements.find(m => m.SampleType == t)?.Id;
		const idSpeed = getId(DataSampleType.WIND_SPEED);
		const idDir = getId(DataSampleType.WIND_DIRECTION);
		const idGustSpeed = includeGust ? getId(DataSampleType.WIND_GUST_SPEED) : undefined;
		const idGustDir = includeGust ? getId(DataSampleType.WIND_GUST_DIRECTION) : undefined;

		const [speed, dir, gustSpeed, gustDirection] = await Promise.all([
			idSpeed ? getHistoricalData(s.Id, idSpeed, from, null) : Promise.resolve(null),
			idDir ? getHistoricalData(s.Id, idDir, from, null) : Promise.resolve(null),
			idGustSpeed ? getHistoricalData(s.Id, idGustSpeed, from, null) : Promise.resolve(null),
			idGustDir ? getHistoricalData(s.Id, idGustDir, from, null) : Promise.resolve(null)
		]);
		return { speed, dir, gustSpeed, gustDirection };

	} catch (error) {
		return { speed: null, dir: null, gustSpeed: null, gustDirection: null }
	}
}

async function loadCurrentData(s: Station, from: Date) {
	try {
		const measurements = s.Measurements.filter(o => o.IsHistValue);
		const getId = (t: DataSampleType) => measurements.find(m => m.SampleType == t)?.Id;
		const idSpeed = getId(DataSampleType.CURRENT_SPEED);
		const idDir = getId(DataSampleType.CURRENT_DIRECTION);

		const [speed, dir] = await Promise.all([
			idSpeed ? getHistoricalData(s.Id, idSpeed, from, null) : Promise.resolve(null),
			idDir ? getHistoricalData(s.Id, idDir, from, null) : Promise.resolve(null)
		]);
		return { speed, dir }
	} catch (error) {
		return { speed: null, dir: null }
	}
}

async function loadTemperatureData(s: Station, from: Date) {
	try {
		const measurements = s.Measurements.filter(o => o.IsHistValue);
		const getId = (t: DataSampleType) => measurements.find(m => m.SampleType == t)?.Id;
		const idAir = getId(DataSampleType.AIRTEMP);
		const idSea = getId(DataSampleType.SEATEMP);

		const [air, sea] = await Promise.all([
			idAir ? getHistoricalData(s.Id, idAir, from, null) : Promise.resolve(null),
			idSea ? getHistoricalData(s.Id, idSea, from, null) : Promise.resolve(null)
		]);
		return { air, sea }
	} catch (error) {
		return { air: null, sea: null }
	}
}
async function loadPressureData(s: Station, from: Date) {
	try {
		const measurements = s.Measurements.filter(o => o.IsHistValue);
		const getId = (t: DataSampleType) => measurements.find(m => m.SampleType == t)?.Id;
		const idStationLevel = getId(DataSampleType.PRESSURE_QFE_PO);
		const idSeaLevel = getId(DataSampleType.PRESSURE_QNH);

		const [stationLevel, seaLevel] = await Promise.all([
			idStationLevel ? getHistoricalData(s.Id, idStationLevel, from, null) : Promise.resolve(null),
			idSeaLevel ? getHistoricalData(s.Id, idSeaLevel, from, null) : Promise.resolve(null)
		]);
		return { stationLevel, seaLevel }
	} catch (error) {
		return { air: null, sea: null }
	}
}

function setMobileHeightByRatio(ratio: number) {
	const px = Math.round(window.innerHeight * ratio);
	sidebarDOM.style.setProperty('--sb-height', `${px}px`);
	updateMobileFullscreenClass();
}

export function openSidebarPeek() {
	if (mmMobile.matches) {
		snapIdx = 0;                    // start at peek
		setMobileHeightByRatio(MOBILE_SNAPS[snapIdx]);
	}
	sidebarDOM.classList.add('open');
	sidebarDOM.setAttribute('aria-hidden', 'false');
	btnCloseDOM?.focus();
}

function onSideBarOpening(s: Station) {
	if (!s || s.Id === currentStation?.Id) {
		return;
	}
	currentStation = s;
	const currentView = getCurrentView();
	const histPanelConfig = currentView?.HistPanelConfiguration ?? null;
	if (histPanelConfig) {
		if (histPanelConfig.ChartRangeHours) {
			rangeSelectDOM.innerHTML = "";
			const rangesAdded = {};
			histPanelConfig.ChartRangeHours.forEach((element) => {
				const hours = clamp(element, 1, 24);
				if (rangesAdded["" + hours] !== undefined) {
					return;
				}
				rangesAdded["" + hours] = true;
				rangeSelectDOM.appendChild(getRangeOption(hours, histPanelConfig.DefaultRangeHours === element));
			});
		}
	}
	const hasMeasurements = currentStation.Measurements.length > 0;
	if (title) {
		title.textContent = s.Name;
		title.title = s.Id;
	}
	if (owner) {
		owner.textContent = s.Owner;
	}
	if (lastUpdate) {
		lastUpdate.textContent = formatDate(getLastUpdatedSampleTimestamp(s));
	}
	if (nowPanel) {
		setNowPanel(s);
		initializeNowCharts(histPanelConfig?.WindRoseIntervalHours != null ? histPanelConfig.WindRoseIntervalHours : 1);
	}
	if (histPanel) {
		histPanel.innerHTML = ``;
	}
	if (!hasMeasurements) {
		let measurementDoms = document.querySelectorAll('[data-measurementsRelated]');
		measurementDoms.forEach(element => {
			const isEnabled = element.getAttribute('data-measurementsRelated');
			if (hasMeasurements && isEnabled === "true") {
				element.classList.remove("hidden");
			}
			if (!hasMeasurements && isEnabled === "true") {
				element.classList.add("hidden");
			}
		});

	}
	initializeCameras();
	initializeHistoricalView(Number(rangeSelectDOM.value));

}

function setNowPanel(s: Station) {
	if (!nowPanel) {
		return;
	}
	nowPanel.innerHTML = "";
	const view = getCurrentView();
	const values = formatNowSections(s);
	if (view?.NowPanelGrouping) {
		view.NowPanelGrouping.forEach((value) => {
			const elementsToAdd = [];
			value.Elements.forEach((element) => {
				const keyElement = DataSampleType[element.Element];
				if (values[keyElement]) {
					elementsToAdd.push(getSectionHTML(element.Title, values[keyElement]));
				}
			});
			if (elementsToAdd.length > 0) {
				elementsToAdd.unshift(getSectionHeader(value.Title));
			}
			elementsToAdd.forEach(element => {
				nowPanel.appendChild(element);
			});
		});
		return;
	}
	// Default
	nowPanel.innerHTML = `<header class="bp-header">
						<span>Last measured values</span>
					</header>`;
	if (values.WIND_SPEED) {
		nowPanel.appendChild(getSectionHTML("Wind speed", values.WIND_SPEED));
	}
	if (values.WIND_DIRECTION) {
		nowPanel.appendChild(getSectionHTML("Wind direction", values.WIND_DIRECTION));
	}
	if (values.WIND_GUST_SPEED) {
		nowPanel.appendChild(getSectionHTML("Gust speed", values.WIND_GUST_SPEED));
	}
	if (values.WIND_GUST_DIRECTION) {
		nowPanel.appendChild(getSectionHTML("Gust direction", values.WIND_GUST_DIRECTION));
	}
	if (values.CURRENT_SPEED) {
		nowPanel.appendChild(getSectionHTML("Current speed", values.CURRENT_SPEED));
	}
	if (values.CURRENT_DIRECTION) {
		nowPanel.appendChild(getSectionHTML("Current direction", values.CURRENT_DIRECTION));
	}
	if (values.AIRTEMP) {
		nowPanel.appendChild(getSectionHTML("Temperature", values.AIRTEMP));
	}
	if (values.SEATEMP) {
		nowPanel.appendChild(getSectionHTML("Sea temperature", values.SEATEMP));
	}
}
function getSectionHeader(title: string): Element {
	const header = document.createElement("header");
	header.classList = "bp-header";
	header.innerHTML = `<span>` + title + `</span>`;
	return header;
}
function getRangeOption(hours: number, selected: boolean): Element {
	const element = document.createElement("option");
	element.value = hours.toString();
	element.selected = selected;
	element.text = getHourText(hours);
	return element;
}

function getSectionHTML(title: string, section: SectionValue): Element {
	const element = document.createElement("section");
	element.classList = "bp-block";
	if (section.valueId) {
		element.id = section.valueId;
	}
	element.title = section.title ?? "";
	element.innerHTML = `
				<h3 class="bp-title">${title}</h3>
						<p class="bp-value">${section.value}</p>`;
	return element;
}
function getStillImageElement(id: string, title: string): Element {
	const imgElement = document.createElement("img");
	imgElement.id = id;
	imgElement.style = "width: 100%";
	const element = document.createElement("section");
	element.classList = "bp-block";
	element.innerHTML = `
				<h3 class="bp-title">${title}</h3><span class="bp-timestamp"></span>
				${imgElement.outerHTML}`;
	return element;
}
function getLiveFeedElement(id: string, title: string, streamUrl: string): Element {
	const videoElement = document.createElement("iframe");
	videoElement.id = id;
	videoElement.style = "aspect-ratio: 16/9";
	videoElement.allowFullscreen = true;
	videoElement.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
	var sourceUrl = streamUrl;
	videoElement.src = sourceUrl;
	const element = document.createElement("section");
	element.classList = "bp-block";
	element.innerHTML = `
				<h3 class="bp-title">${title}</h3><span class="bp-timestamp"></span>
				${videoElement.outerHTML}`;
	return element;
}

function getUniqueMvId(s: DataSample) { return (s.StationId + ":" + s.MvId).toUpperCase(); }

type SectionValue = { value: string, title: string | null, valueId: string | null }

function formatSection(type: DataSampleType): SectionValue | null {
	if (!currentStation) {
		return null;
	}
	var sample = getLatestSample(currentStation, type);
	if (sample) {
		let value = formatSample(sample);
		switch (sample.Type) {
			case DataSampleType.CURRENT_DIRECTION:
			case DataSampleType.WIND_DIRECTION:
			case DataSampleType.WIND_GUST_DIRECTION:
				value = `${formatSample(sample, true, { showText: false, abbr: false })} - ${formatSample(sample, true, { showText: true, abbr: false })}`;
		}
		return {
			value: value,
			title: formatDate(sample.Timestamp),
			valueId: getUniqueMvId(sample)
		};
	}
	return null;
}
function formatNowSections(s: Station): Record<keyof typeof DataSampleType, SectionValue | null> {
	const map = new Map<DataSampleType, SectionValue | null>();
	(Object.keys(DataSampleType) as Array<keyof typeof DataSampleType>).map((key) => { map.set(DataSampleType[key], formatSection(DataSampleType[key])); });
	const record: Record<keyof typeof DataSampleType, SectionValue | null> = {
		AIRTEMP: map.get(DataSampleType.AIRTEMP) ?? null,
		WIND_SPEED: map.get(DataSampleType.WIND_SPEED) ?? null,
		WIND_GUST_DIRECTION: map.get(DataSampleType.WIND_GUST_DIRECTION) ?? null,
		WIND_GUST_SPEED: map.get(DataSampleType.WIND_GUST_SPEED) ?? null,
		WIND_DIRECTION: map.get(DataSampleType.WIND_DIRECTION) ?? null,
		CURRENT_DIRECTION: map.get(DataSampleType.CURRENT_DIRECTION) ?? null,
		CURRENT_SPEED: map.get(DataSampleType.CURRENT_SPEED) ?? null,
		DEWPOINT: map.get(DataSampleType.DEWPOINT) ?? null,
		HUMIDITY: map.get(DataSampleType.HUMIDITY) ?? null,
		PRESSURE_CHANGE_AA: map.get(DataSampleType.PRESSURE_CHANGE_AA) ?? null,
		PRESSURE_QFE_PO: map.get(DataSampleType.PRESSURE_QFE_PO) ?? null,
		PRESSURE_QNH: map.get(DataSampleType.PRESSURE_QNH) ?? null,
		PRESSURE_TENDENCY_PP: map.get(DataSampleType.PRESSURE_TENDENCY_PP) ?? null,
		RAIN_ACCUMULATED: map.get(DataSampleType.RAIN_ACCUMULATED) ?? null,
		VISIBILITY: map.get(DataSampleType.VISIBILITY) ?? null,
		WATER_LEVEL: map.get(DataSampleType.WATER_LEVEL) ?? null,
		NONE: null,
		LIVEFEED: null,
		SEATEMP: map.get(DataSampleType.SEATEMP) ?? null,
		STILLIMAGE: null
	};
	return record;
}

handleY?.addEventListener('click', () => {
	if (!mmMobile.matches || !sidebarDOM.classList.contains('open')) return;
	snapIdx = Math.min(snapIdx + 1, MOBILE_SNAPS.length - 1);
	setMobileHeightByRatio(MOBILE_SNAPS[snapIdx]);
});
function updateMobileFullscreenClass() {
	if (!mmMobile.matches) { sidebarDOM.classList.remove('sb--fullscreen'); return; }
	const maxH = effectiveMaxHeight();                   // 100dvh in px (your function)
	const curH = sidebarDOM.getBoundingClientRect().height;
	const nearMax = Math.abs(curH - maxH) <= 2;          // fuzzy compare
	sidebarDOM.classList.toggle('sb--fullscreen', nearMax);
}

export function GetCurrentStation() {
	return currentStation;
}

export function updateCamera(cameraId: string) {
	if (currentStation === null) {
		return;
	}
	const cameraFeed = document.getElementById(cameraId.toUpperCase()) as HTMLImageElement;
	if (cameraFeed) {
		cameraFeed.src = getLastImageUrl(cameraId);
		getImageTimestamp(cameraId).then((timestamp) => {
			cameraFeed.title = timestamp ?? "";
			const parent = cameraFeed.parentElement;
			if (parent) {
				const timestampElement = parent.getElementsByClassName("bp-timestamp");
				if (timestampElement && timestampElement.length > 0 && timestampElement[0]) {
					timestampElement[0].textContent = timestamp;
					flashElement(timestampElement[0] as HTMLElement);
				}
			}
		});
	}
}
export function updateSideBar(sample: DataSample) {
	if (currentStation?.Id.toUpperCase() === sample.StationId.toUpperCase()) {

		if (lastUpdate) {
			lastUpdate.textContent = formatDate(getLastUpdatedSampleTimestamp(currentStation));
			flashElement(lastUpdate);
		}

		// Update now-section
		const section = document.getElementById(getUniqueMvId(sample));
		const valueNode = section?.querySelector<HTMLElement>(".bp-value");
		if (section && valueNode) {
			const format = formatSection(sample.Type);
			flashElement(section);
			section.title = format?.title ?? "";
			valueNode.innerHTML = format?.value ?? "";
		}
		const from = rangeSelectDOM ? new Date(new Date(Date.now() - Number(rangeSelectDOM.value) * 3600 * 1000)).getTime() : null;
		switch (sample.Type) {
			// SORRY! Quick 'n' dirty
			case DataSampleType.WIND_SPEED:
			case DataSampleType.WIND_DIRECTION:
			case DataSampleType.WIND_GUST_DIRECTION:
			case DataSampleType.WIND_GUST_SPEED:
				if (windChart) {
					const seriesIndex = windChart.series.findIndex(o => o.name === CHARTNAME_BY_TYPE[sample.Type]);
					if (seriesIndex !== -1) {
						const xySample = toHighchartsXY([sample]);
						if (xySample) {
							windChart.series[seriesIndex].addPoint(xySample[0], true, false, true);
							if (from) {
								lockXAxis(windChart, from);
							}
						}
					}
				}
				if (windRose) {
					let dirSample: DataSample | null = null;
					let speedSample: DataSample | null = null;

					if (sample.Type === DataSampleType.WIND_SPEED) {
						speedSample = sample;
						dirSample = getLatestSample(currentStation, DataSampleType.WIND_DIRECTION);
					}
					if (sample.Type === DataSampleType.WIND_DIRECTION) {
						speedSample = getLatestSample(currentStation, DataSampleType.WIND_SPEED);
						dirSample = sample;
					}
					if (dirSample && speedSample && dirSample.Timestamp === speedSample.Timestamp) {
						const firstPoint = windRose.series[0].data[0] as any;
						let shiftPoints = false;
						if (firstPoint !== undefined) {
							shiftPoints = getFadeValue(firstPoint.date, 60) === 1;
						}
						roseChart.addPointWithAnimation(windRose, roseChart.createRosePoint(dirSample, speedSample, 60), shiftPoints);
					}
				}
				break;
		}
	}
}

function initializeCameras() {
	if (!currentStation) {
		return;
	}
	if (cameraPanel) {
		cameraPanel.classList.add("hidden");
		if (currentStation.HasStillImages || currentStation.HasLiveFeed) {
			cameraPanel.classList.remove("hidden");

			cameraPanel.innerHTML = `<header class="bp-header">
						<span>Camera</span>
					</header>`;
			for (const cam of currentStation.Cameras ?? []) {
				if (!cam.Enabled) {
					continue;
				}
				const source = currentStation.Id.substring(0, currentStation.Id.lastIndexOf(":"));
				const camId = source + ":" + cam.Id;
				if (!cam.IsLive) {
					cameraPanel.appendChild(getStillImageElement(camId.toUpperCase(), cam.Name));
					updateCamera(camId);
				} else {
					cameraPanel.appendChild(getLiveFeedElement(camId.toUpperCase(), cam.Name, cam.DirectUrl));
				}
			}
		}
	}
}

export function flashElement(element: HTMLElement | undefined) {
	if (!element) {
		return;
	}
	element.classList.add("blink");
	setTimeout(() => element.classList.remove("blink"), 690);
}
